/* PDP slide-in drawer.

   One panel of steps, rendered by the theme — nothing is fetched.

   Open/close mirrors cart-drawer.js: the .active class is added inside a
   setTimeout (the cart's comment notes the transition does not always fire
   without one), focus is trapped once the slide finishes, and the body gets
   .overflow-hidden for the duration. Escape and a backdrop click close it.

   trapFocus/removeTrapFocus are globals from global.js, same as the cart
   drawer uses; guarded in case this snippet ever renders without it. */
(function () {
  if (window.pdpDrawerInit) return;
  window.pdpDrawerInit = true;

  /* The cart drawer sits at body level. This one is rendered inside the product
     section, where any transformed or filtered ancestor would make position:
     fixed resolve against that ancestor instead of the viewport — the panel
     would be clipped or land in the wrong place. Hoisting it to <body> on load
     puts it in the same position the cart drawer is already in. */
  function hoist(root) {
    (root || document).querySelectorAll('.pdp-drawer').forEach(function (drawer) {
      if (drawer.parentElement === document.body) return;
      // A section re-render in the theme editor produces a fresh copy; drop the
      // one already hoisted so the id stays unique.
      var existing = document.body.querySelector(
        ':scope > .pdp-drawer#' + CSS.escape(drawer.id)
      );
      if (existing) existing.remove();
      document.body.appendChild(drawer);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { hoist(); });
  } else {
    hoist();
  }
  document.addEventListener('shopify:section:load', function (event) {
    hoist(event.target);
  });

  /* Panels. Only one is mounted at a time; the outgoing one fades before the
     incoming one fades in, so the frame stays put and only the content moves.
     Without a transition to wait on (reduced motion, or no panel showing yet)
     the swap is immediate. */
  var FADE = 160;

  function panels(drawer) {
    return drawer.querySelectorAll('[data-pdp-drawer-panel-body]');
  }

  /* Autoplay only fires for a video the browser can lay out, and every panel
     starts at display: none — so the option videos sat on their first frame
     until the panel they live in was mounted. Kick them then, and stop the
     ones being put away. playsinline is set here rather than trusted to the
     tag, since iOS takes a video without it fullscreen.

     Option videos are served without autoplay and with preload none, so a
     closed drawer costs nothing but the poster; data-pdp-autoplay marks the
     ones that should still play the moment their panel is shown. */
  function playVideos(el, on) {
    el.querySelectorAll('video').forEach(function (video) {
      if (!on) {
        video.pause();
        return;
      }
      if (!video.autoplay && !video.closest('[data-pdp-autoplay]')) return;
      video.playsInline = true;
      video.muted = true;
      var playing = video.play();
      if (playing && playing.catch) playing.catch(function () {});
    });
  }

  function show(drawer, panel) {
    panels(drawer).forEach(function (el) {
      var on = el.dataset.pdpDrawerPanelBody === panel;
      el.classList.toggle('pdp-drawer__panel--current', on);
      el.classList.toggle('pdp-drawer__panel--in', on);
      if (on) el.scrollTop = 0;
      playVideos(el, on);
    });

    drawer.querySelectorAll('[data-pdp-drawer-tab]').forEach(function (tab) {
      tab.setAttribute(
        'aria-selected',
        tab.dataset.pdpDrawerTab === panel ? 'true' : 'false'
      );
    });

    var heading = drawer.querySelector('[data-pdp-drawer-heading]');
    var tab = drawer.querySelector('[data-pdp-drawer-tab="' + CSS.escape(panel) + '"]');
    if (heading && tab) heading.textContent = tab.dataset.pdpDrawerTitle || '';

    drawer.dataset.pdpDrawerCurrent = panel;

    // The arrows can only be resolved once the track has a width.
    drawer.querySelectorAll('[data-pdp-carousel]').forEach(syncNav);
  }

  function reducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function select(drawer, panel, animate) {
    if (!panel || drawer.dataset.pdpDrawerCurrent === panel) return;
    warm(drawer, panel);

    var current = drawer.querySelector('.pdp-drawer__panel--current');
    if (!animate || !current || reducedMotion()) {
      show(drawer, panel);
      return;
    }

    current.classList.remove('pdp-drawer__panel--in');
    setTimeout(function () {
      show(drawer, panel);
      var next = drawer.querySelector('.pdp-drawer__panel--current');
      if (!next) return;
      // Mounted with the class already on, so it would arrive at full opacity
      // with nothing to transition from; take it off for one frame.
      next.classList.remove('pdp-drawer__panel--in');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          next.classList.add('pdp-drawer__panel--in');
        });
      });
    }, FADE);
  }

  /* Scroll lock.

     The theme's is `overflow: hidden` on <body>, and below 750px base.css
     adds `height: 100%` to <html> and <body> with it. A scroller that can no
     longer overflow has nowhere to be scrolled to, so the page snaps to the
     top the moment the class lands — visibly, before the panel has covered
     it, and putting it back afterwards cannot undo a jump already seen.

     So the page is pinned instead: <body> goes fixed at minus its scroll
     offset, which leaves every pixel exactly where it was. Nothing moves on
     the way in, and unpinning restores the offset on the way out.

     The restore has to be instant. component-jump-to.css sets
     `scroll-behavior: smooth` on <html>, which would animate it into a
     visible scroll back down the page, so it is turned off for the one call. */
  var scrollTop = 0;

  function lockScroll() {
    scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
    document.body.style.top = -scrollTop + 'px';
    document.body.classList.add('pdp-drawer-locked');
  }

  function unlockScroll() {
    if (!document.body.classList.contains('pdp-drawer-locked')) return;
    document.body.classList.remove('pdp-drawer-locked');
    document.body.style.top = '';

    var root = document.documentElement;
    var behavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    window.scrollTo(0, scrollTop);
    root.style.scrollBehavior = behavior;
  }

  /* Images in a panel are lazy, and a panel is display: none until its tab is
     picked — so nothing is ever near the viewport and the browser holds every
     request until the drawer opens. Six certification marks then arrive at
     once, visibly.

     They stay lazy in the markup, since a shopper who never opens a drawer
     should not pay for its artwork. Instead the panel is warmed the moment
     someone shows intent — a pointer over the row, or a focus on it — which
     buys the fetch a head start on the 400ms the drawer takes to slide in. */
  function warm(drawer, panel) {
    if (!drawer) return;
    drawer.querySelectorAll('[data-pdp-drawer-panel-body]').forEach(function (el) {
      if (panel && el.dataset.pdpDrawerPanelBody !== panel) return;
      el.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
        img.loading = 'eager';
      });
      // Same for the option videos, held at preload none until now.
      el.querySelectorAll('[data-pdp-autoplay] video[preload="none"]').forEach(function (video) {
        video.preload = 'auto';
        // Assigning preload does not start a file the browser has not touched yet.
        video.load();
      });
    });
  }

  ['pointerenter', 'focusin'].forEach(function (type) {
    document.addEventListener(
      type,
      function (event) {
        var trigger =
          event.target.closest && event.target.closest('[data-pdp-drawer-open]');
        if (!trigger) return;
        warm(
          document.getElementById(trigger.dataset.pdpDrawerTarget),
          trigger.dataset.pdpDrawerPanel
        );
      },
      true
    );
  });

  function open(drawer, trigger, panel) {
    var isOpen = drawer.classList.contains('active');
    var first = panels(drawer)[0];
    var target =
      panel ||
      drawer.dataset.pdpDrawerCurrent ||
      (first && first.dataset.pdpDrawerPanelBody);

    warm(drawer, target);

    // A row clicked while the drawer is already open reads as a tab switch.
    select(drawer, target, isOpen);
    if (isOpen) return;

    drawer.pdpDrawerTrigger = trigger || null;
    setTimeout(function () {
      drawer.classList.add('animate', 'active');
    });

    drawer.addEventListener(
      'transitionend',
      function () {
        if (typeof trapFocus === 'function') {
          trapFocus(drawer, drawer.querySelector('.pdp-drawer__close'));
        }
      },
      { once: true }
    );

    lockScroll();

    // The track has no measurable width until the panel is on screen, so the
    // arrows can only be resolved once it is.
    drawer.querySelectorAll('[data-pdp-carousel]').forEach(syncNav);
  }

  function close(drawer) {
    if (!drawer || !drawer.classList.contains('active')) return;
    drawer.classList.remove('active');
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    unlockScroll();
    if (drawer.pdpDrawerTrigger) {
      drawer.pdpDrawerTrigger.focus({ preventScroll: true });
      drawer.pdpDrawerTrigger = null;
    }
  }

  /* Carousel. Scrolling the snap track by one pane, rather than a Swiper
     instance per step — same behaviour, no bundle, and nothing to initialise
     when the drawer opens. Any [data-pdp-carousel] wrapping a track and a
     pair of arrows gets it: the step media and the do / do-not examples. */
  function syncNav(media) {
    var track = media.querySelector('[data-pdp-step-track]');
    var prev = media.querySelector('[data-pdp-step-prev]');
    var next = media.querySelector('[data-pdp-step-next]');
    if (!track || !prev || !next) return;
    // A rounding slack of 2px: smooth scrolling rarely lands on an exact pixel.
    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
  }

  function step(media, dir) {
    var track = media.querySelector('[data-pdp-step-track]');
    if (!track) return;
    track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
  }

  document.addEventListener(
    'scroll',
    function (event) {
      var track = event.target;
      if (track.dataset && track.dataset.pdpStepTrack !== undefined) {
        syncNav(track.closest('[data-pdp-carousel]'));
      }
    },
    true
  );

  document.addEventListener('click', function (event) {
    var nav = event.target.closest('[data-pdp-step-prev], [data-pdp-step-next]');
    if (nav) {
      var media = nav.closest('[data-pdp-carousel]');
      step(media, nav.hasAttribute('data-pdp-step-next') ? 1 : -1);
      return;
    }

    var trigger = event.target.closest('[data-pdp-drawer-open]');
    if (trigger) {
      /* Hoist again on the way in. The load-time pass only sees the markup the
         page was served with, and the theme re-renders product-info on a
         variant change — which puts a fresh, un-hoisted copy of the drawer
         back inside the sticky info column. Sticky with a z-index is a
         stacking context, so that copy opens underneath the site header. */
      hoist();
      /* A trigger inside the product section names the drawer by id. One
         further down the page — the install section's "Full Instructions"
         button — has no way to know that id, and there is only ever one
         drawer, so an unnamed trigger takes it. */
      var drawer =
        document.getElementById(trigger.dataset.pdpDrawerTarget || '') ||
        document.querySelector('.pdp-drawer');
      if (drawer) {
        event.preventDefault();
        open(drawer, trigger, trigger.dataset.pdpDrawerPanel);
      }
      return;
    }


    var tab = event.target.closest('[data-pdp-drawer-tab]');
    if (tab) {
      var tabDrawer = tab.closest('.pdp-drawer');
      if (tabDrawer) select(tabDrawer, tab.dataset.pdpDrawerTab, true);
      return;
    }

    if (event.target.closest('[data-pdp-drawer-close]')) {
      close(event.target.closest('.pdp-drawer'));
      return;
    }

    // The overlay fills the viewport and the panel only part of it, so a click
    // that lands on the overlay itself is a click outside.
    if (event.target.classList && event.target.classList.contains('pdp-drawer')) {
      close(event.target);
    }
  });

  document.addEventListener('keyup', function (event) {
    if (event.key !== 'Escape' && event.code !== 'Escape') return;
    close(document.querySelector('.pdp-drawer.active'));
  });

})();
